import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { useFormSave } from '../hooks/useFormSave';
import { useProfileSearch } from '../hooks/useProfileSearch';
import { ApplicationForm, Researcher } from '../types';
import {
  internationalInstitutions,
  nationalInstitutions,
  otherInstitutionsOption,
} from '../utils/institutions';
import { isConsolidated } from '../utils/classificationUtils';
import { combineClasses } from '../utils/combineClasses';
import Button from 'shared/components/UC/Button';
import SearchableSelect from 'shared/components/SearchableSelect';
import Select from 'shared/components/UC/Select';
import styles from './TeamInformation.module.css';

const MONTHS_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

// Helper function to normalize Chilean RUT (remove dots, keep only digits-verificationDigit)
const normalizeChileanRUT = (rut: string): string => {
  if (!rut || !rut.trim()) {
    return '';
  }

  // Remove any existing formatting (dots, spaces)
  const cleanRut = rut.replace(/[^0-9kK]/g, '');

  // Check if it looks like a Chilean RUT (8-9 digits)
  if (cleanRut.length < 8 || cleanRut.length > 9) {
    return rut; // Return original if it doesn't look like a Chilean RUT
  }

  // Separate the verification digit
  const rutBody = cleanRut.slice(0, -1);
  const verificationDigit = cleanRut.slice(-1).toLowerCase();

  // Return normalized format: XXXXXXXX-Y (no dots, just hyphen)
  return `${rutBody}-${verificationDigit}`;
};

// Helper function to format Chilean RUT for display
const formatChileanRUT = (rut: string): string => {
  if (!rut || !rut.trim()) {
    return '';
  }

  // Remove any existing formatting
  const cleanRut = rut.replace(/[^0-9kK]/g, '');

  // Check if it looks like a Chilean RUT (8-9 digits)
  if (cleanRut.length < 8 || cleanRut.length > 9) {
    return rut; // Return original if it doesn't look like a Chilean RUT
  }

  // Separate the verification digit
  const rutBody = cleanRut.slice(0, -1);
  const verificationDigit = cleanRut.slice(-1);

  // Add dots to the RUT body
  const formattedBody = rutBody.replace(/(\d)(?=(\d{3})+$)/g, '$1.');

  return `${formattedBody}-${verificationDigit}`;
};

// Helper function to check if a RUN should be formatted as Chilean RUT
const shouldFormatAsChileanRUT = (
  role: string | undefined,
  documentType?: string
): boolean => {
  if (role === 'IP' || role === 'IA') return true;
  if (role === 'COI' && documentType === 'run') return true;
  return false;
};

// Helper function to check if a string looks like a Chilean RUT (not a passport)
const isChileanRUT = (run: string): boolean => {
  const cleanRut = run.replace(/[^0-9kK]/g, '');
  // Chilean RUT has 8-9 digits (including verification digit)
  return cleanRut.length >= 8 && cleanRut.length <= 9;
};

// Helper function to validate Chilean RUT using the verification digit algorithm
const validateChileanRUT = (rut: string): boolean => {
  // Remove formatting
  const cleanRut = rut.replace(/[^0-9kK]/g, '').toUpperCase();

  // Check length
  if (cleanRut.length < 8 || cleanRut.length > 9) {
    return false;
  }

  // Separate body and verification digit
  const rutBody = cleanRut.slice(0, -1);
  const verificationDigit = cleanRut.slice(-1);

  // Calculate expected verification digit
  let sum = 0;
  let multiplier = 2;

  // Calculate from right to left
  for (let i = rutBody.length - 1; i >= 0; i--) {
    sum += parseInt(rutBody[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const remainder = sum % 11;
  const calculatedDigit = 11 - remainder;

  // Convert to verification digit
  let expectedDigit: string;
  if (calculatedDigit === 11) {
    expectedDigit = '0';
  } else if (calculatedDigit === 10) {
    expectedDigit = 'K';
  } else {
    expectedDigit = calculatedDigit.toString();
  }

  return verificationDigit === expectedDigit;
};

interface TeamInformationProps {
  readOnly?: boolean;
}

const TeamInformation: React.FC<TeamInformationProps> = ({
  readOnly = false,
}) => {
  // Initialize form save hook for context awareness
  const { saveForm, setUpdating } = useFormSave({ stepId: 'team' });
  const saveFormRef = useRef(saveForm);

  // Keep ref updated
  useEffect(() => {
    saveFormRef.current = saveForm;
  }, [saveForm]);

  const { authState } = useAuth();
  const { profile, loading, error, searchProfile, clearResults } =
    useProfileSearch();
  const [searchingForIndex, setSearchingForIndex] = useState<number | null>(
    null
  );
  const [runInputs, setRunInputs] = useState<Record<number, string>>({});
  const [coiSearchResults, setCOISearchResults] = useState<
    Record<number, 'searching' | 'found' | 'not-found' | 'no-search'>
  >({});
  const [customInstitutions, setCustomInstitutions] = useState<
    Record<number, string>
  >({});

  const {
    register,
    formState: { errors },
    watch,
    setValue,
    trigger,
    clearErrors,
    setError,
  } = useFormContext<ApplicationForm>();

  const watchedCoInvestigators = watch('coInvestigators');
  const coInvestigators = useMemo(
    () => watchedCoInvestigators || [],
    [watchedCoInvestigators]
  );
  const principalInvestigator = watch('principalInvestigator');

  // Load principal investigator data from logged-in user
  useEffect(() => {
    if (
      authState.user &&
      authState.user.userProfile &&
      !(
        principalInvestigator?.run &&
        principalInvestigator?.name &&
        principalInvestigator?.email
      )
    ) {
      const userProfile = authState.user.userProfile;
      const piData: Researcher = {
        name: authState.user.name,
        email: authState.user.email,
        run: userProfile.run || '',
        affiliation: 'Pontificia Universidad Católica de Chile',
        facultadUC: userProfile.facultadUC || '',
        unidadAcademicaUC: userProfile.unidadAcademica || '',
        doctorateYear: userProfile.doctorateYear || new Date().getFullYear(),
        doctorateMonth: userProfile.doctorateMonth || undefined,
        type: userProfile.researcherType || 'emerging',
        role: 'IR' as const,
      };

      setValue('principalInvestigator', piData);
    }
  }, [principalInvestigator, authState.user, setValue]);

  // Handle profile search result for IP team members
  useEffect(() => {
    const handleProfileResult = async () => {
      if (profile && searchingForIndex !== null && authState.token) {
        const currentRole = watch(`coInvestigators.${searchingForIndex}.role`);

        // Handle UC role search results (IP or IA)
        if (currentRole === 'IP' || currentRole === 'IA') {
          // Check if the user can apply
          if (!profile.canApply) {
            // Show the platform message for longer
            setTimeout(() => {
              setSearchingForIndex(null);
              clearResults();
            }, 3000);
            return;
          }

          // User can apply - populate the form fields
          const memberData: Researcher = {
            name: profile.name,
            email: '',
            run: profile.run,
            affiliation: 'Pontificia Universidad Católica de Chile',
            facultadUC: profile.facultadUC || '',
            unidadAcademicaUC: profile.unidadAcademica || '',
            ...(currentRole === 'IP'
              ? {
                  doctorateYear: profile.doctorateYear || new Date().getFullYear(),
                  doctorateMonth: profile.doctorateMonth ?? undefined,
                  type: isConsolidated(profile.doctorateYear, profile.doctorateMonth)
                    ? 'consolidated'
                    : 'emerging',
                }
              : { type: 'emerging' as const }),
            role: currentRole as 'IP' | 'IA',
          };

          // Prevent auto-save from triggering during programmatic update
          setUpdating(true);
          setValue(`coInvestigators.${searchingForIndex}`, memberData);
          setUpdating(false);

          // Manual save after programmatic update
          const saveResult = await saveFormRef.current();
          if (
            saveResult?.success === false &&
            saveResult?.reason === 'validation_errors'
          ) {
            console.warn(
              'Profile search update blocked due to validation errors'
            );
          }

          // Show success feedback briefly
          setTimeout(() => {
            setSearchingForIndex(null);
            clearResults();
          }, 3000);
        }

        // Handle COI search results - just show warning if profile found
        if (currentRole === 'COI') {
          setCOISearchResults((prev) => ({
            ...prev,
            [searchingForIndex]: 'found',
          }));
          // Show warning for longer since user needs to read it
        }
      }
    };

    handleProfileResult();
  }, [
    profile,
    searchingForIndex,
    setValue,
    clearResults,
    authState.token,
    setUpdating,
    watch,
  ]);

  // Handle when search completes with no profile found (for COI)
  useEffect(() => {
    if (!profile && !loading && !error && searchingForIndex !== null) {
      const currentRole = watch(`coInvestigators.${searchingForIndex}.role`);
      if (currentRole === 'COI') {
        setCOISearchResults((prev) => ({
          ...prev,
          [searchingForIndex]: 'not-found',
        }));
        setSearchingForIndex(null);
      }
    }
  }, [profile, loading, error, searchingForIndex, watch]);

  const addCoInvestigator = () => {
    if (readOnly) return;
    const newCoInvestigator: Researcher = {
      name: '',
      email: '',
      run: '',
      documentType: 'run',
      affiliation: '',
      type: 'emerging',
      role: undefined,
      institutionType: undefined,
    };
    setValue('coInvestigators', [...coInvestigators, newCoInvestigator]);
  };

  const removeCoInvestigator = async (index: number) => {
    if (readOnly) return; // Don't allow changes in read-only mode

    const investigatorToRemove = coInvestigators[index];
    if (!investigatorToRemove) return;

    // Prevent auto-save from triggering during programmatic update
    setUpdating(true);

    // Remove from coInvestigators array
    const updated = coInvestigators.filter((_, i) => i !== index);
    setValue('coInvestigators', updated);

    // Clean up custom institutions state for this index
    setCustomInstitutions((prev) => {
      const newCustom = { ...prev };
      delete newCustom[index];
      // Re-index remaining entries
      const reindexed: Record<number, string> = {};
      Object.entries(newCustom).forEach(([key, value]) => {
        const oldIndex = parseInt(key);
        if (oldIndex > index) {
          reindexed[oldIndex - 1] = value;
        } else {
          reindexed[oldIndex] = value;
        }
      });
      return reindexed;
    });

    // Let useIPSync handle ipCvData cleanup automatically
    // Note: COI commitment letters are handled by array index, so they get cleaned up automatically
    // when the coInvestigators array is filtered

    setUpdating(false);

    // Save team section
    const saveResult = await saveFormRef.current();
    if (
      saveResult?.success === false &&
      saveResult?.reason === 'validation_errors'
    ) {
      console.warn('Team member removal save blocked due to validation errors');
    }
  };
  // Determine team type based on IR + IPs only (IA and COI do not affect classification).
  // DEV NOTE — threshold de clasificación: enero 2020 (antes de enero 2020 = Consolidado).
  // See src/utils/classificationUtils.ts for threshold definition (CLASSIFICATION_THRESHOLD).
  // La tabla de datos de integración (AJ-36/Denise) aún dice "corte 2018" en el campo
  // "Año en que obtuvo el grado / título". Debe actualizarse a 2020 antes de conectar
  // la integración, o el sistema clasificará incorrectamente. Email de alineación enviado
  // el 02-07-2026. Ref: documento Javiera 01-07-2026, sección 2 (Categorías A/B).
  // consolidated wins: if IR or any IP is Consolidado → consolidated.
  const getTeamType = (): 'consolidated' | 'emerging' => {
    const irIsConsolidated = isConsolidated(
      principalInvestigator?.doctorateYear,
      principalInvestigator?.doctorateMonth
    );

    const ipIsConsolidated = coInvestigators
      .filter((c: Researcher) => c.role === 'IP')
      .some((c: Researcher) => isConsolidated(c.doctorateYear, c.doctorateMonth));

    return irIsConsolidated || ipIsConsolidated ? 'consolidated' : 'emerging';
  };

  const teamType = getTeamType();

  const hasInternationalCOI = coInvestigators.some(
    (ci) => ci.role === 'COI' && ci.institutionType === 'Internacional'
  );

  // Count UC co-researchers (IP + IA). Max 3 in coInvestigators (IR takes the 4th UC slot).
  const ucCoResearcherCount = coInvestigators.filter(
    (_, index) => ['IP', 'IA'].includes(watch(`coInvestigators.${index}.role`) ?? '')
  ).length;

  // Helper function to determine if COI fields should be enabled
  const shouldEnableCOIFields = useCallback(
    (index: number): boolean => {
      const role = watch(`coInvestigators.${index}.role`);

      if (role !== 'COI') {
        return false; // Not COI role
      }

      const run =
        runInputs[index] || watch(`coInvestigators.${index}.run`) || '';

      // If no RUN entered yet, enable fields (assume valid)
      if (!run.trim()) {
        return true;
      }

      // If international passport, always enable
      if (!isChileanRUT(run)) {
        return true;
      }

      // Chilean RUT - only disable if we found a profile
      const searchResult = coiSearchResults[index];
      return searchResult !== 'found'; // Disable only when profile is found
    },
    [runInputs, watch, coiSearchResults]
  );

  // Clear COI fields when a profile is found (fields become disabled)
  useEffect(() => {
    coInvestigators.forEach((_, index) => {
      const role = watch(`coInvestigators.${index}.role`);
      const searchResult = coiSearchResults[index];

      // Only clear fields when profile is found (rare case)
      if (role === 'COI' && searchResult === 'found') {
        const currentName = watch(`coInvestigators.${index}.name`);
        const currentEmail = watch(`coInvestigators.${index}.email`);
        const currentAffiliation = watch(
          `coInvestigators.${index}.affiliation`
        );
        const currentInstitutionType = watch(
          `coInvestigators.${index}.institutionType`
        );

        if (
          currentName ||
          currentEmail ||
          currentAffiliation ||
          currentInstitutionType
        ) {
          setValue(`coInvestigators.${index}.name`, '');
          setValue(`coInvestigators.${index}.email`, '');
          setValue(`coInvestigators.${index}.affiliation`, '');
          setValue(`coInvestigators.${index}.institutionType`, undefined);
        }
      }
    });
  }, [coInvestigators, watch, setValue, coiSearchResults]);

  // Handle loading existing data with custom institutions
  useEffect(() => {
    coInvestigators.forEach((_, index) => {
      const role = watch(`coInvestigators.${index}.role`);
      const affiliation = watch(`coInvestigators.${index}.affiliation`);

      if (
        role === 'COI' &&
        affiliation?.startsWith(`${otherInstitutionsOption} - `)
      ) {
        const customValue = affiliation.substring(
          `${otherInstitutionsOption} - `.length
        );
        // Only set the custom institution state if it's not already set
        if (customInstitutions[index] !== customValue) {
          setCustomInstitutions((prev) => ({
            ...prev,
            [index]: customValue,
          }));
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coInvestigators.length]); // Only run when co-investigators are added/removed

  // Re-validate justification whenever international COI status changes
  useEffect(() => {
    trigger('internationalCoiJustification');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInternationalCOI]);

  // Handle RUN search for IP members
  const handleRunSearch = async (index: number, run: string) => {
    if (readOnly || !run.trim() || !authState.token) return; // Don't allow search in read-only mode

    setSearchingForIndex(index);
    setRunInputs((prev) => ({ ...prev, [index]: run }));

    try {
      await searchProfile(run.trim(), authState.token);
    } catch {
      // If search fails, clear the form data for this investigator
      await clearIPFormData(index);
    }
  };

  // Clear IP form data when search fails - but keep the RUN that was entered
  const clearIPFormData = useCallback(
    async (index: number) => {
      setUpdating(true);
      setValue(`coInvestigators.${index}.name`, '');
      setValue(`coInvestigators.${index}.email`, '');
      // DON'T clear the RUN - keep what the user typed
      setValue(`coInvestigators.${index}.facultadUC`, '');
      setValue(`coInvestigators.${index}.unidadAcademicaUC`, '');
      setValue(`coInvestigators.${index}.doctorateYear`, '' as any);
      setUpdating(false);
      // DON'T clear the RUN input state either
      // Manual save after programmatic update
      const saveResult = await saveFormRef.current();
      if (
        saveResult?.success === false &&
        saveResult?.reason === 'validation_errors'
      ) {
        console.warn(
          'IP form data clear save blocked due to validation errors'
        );
      }
    },
    [setValue, setUpdating]
  );

  // Handle role change with proper field management
  const handleRoleChange = (index: number, newRole: string) => {
    if (readOnly) return; // Don't allow changes in read-only mode

    setValue(`coInvestigators.${index}.role`, newRole as 'IP' | 'IA' | 'COI');

    setSearchingForIndex(null);
    clearResults();
    if (newRole === 'IP') {
      // Clear all fields for IP - they will be populated via RUN search
      // IP must always use RUN
      setValue(`coInvestigators.${index}.documentType`, 'run');
      setValue(`coInvestigators.${index}.name`, '');
      setValue(`coInvestigators.${index}.email`, '');
      setValue(`coInvestigators.${index}.run`, '');
      setValue(
        `coInvestigators.${index}.affiliation`,
        'Pontificia Universidad Católica de Chile'
      );
      setValue(`coInvestigators.${index}.facultadUC`, '');
      setValue(`coInvestigators.${index}.unidadAcademicaUC`, '');
      setValue(`coInvestigators.${index}.doctorateYear`, '' as any);
      setValue(`coInvestigators.${index}.institutionType`, undefined);

      // Clear RUN input
      setRunInputs((prev) => ({ ...prev, [index]: '' }));

      // Clear custom institution
      setCustomInstitutions((prev) => {
        const newCustom = { ...prev };
        delete newCustom[index];
        return newCustom;
      });
    } else if (newRole === 'IA') {
      // IA: same as IP but no doctorateYear (IA doesn't have a doctorate)
      setValue(`coInvestigators.${index}.documentType`, 'run');
      setValue(`coInvestigators.${index}.name`, '');
      setValue(`coInvestigators.${index}.email`, '');
      setValue(`coInvestigators.${index}.run`, '');
      setValue(
        `coInvestigators.${index}.affiliation`,
        'Pontificia Universidad Católica de Chile'
      );
      setValue(`coInvestigators.${index}.facultadUC`, '');
      setValue(`coInvestigators.${index}.unidadAcademicaUC`, '');
      setValue(`coInvestigators.${index}.institutionType`, undefined);

      setRunInputs((prev) => ({ ...prev, [index]: '' }));
      setCustomInstitutions((prev) => {
        const newCustom = { ...prev };
        delete newCustom[index];
        return newCustom;
      });
    } else if (newRole === 'COI') {
      // Clear ALL fields when switching to COI
      setValue(`coInvestigators.${index}.documentType`, 'run'); // Default to RUN
      setValue(`coInvestigators.${index}.name`, '');
      setValue(`coInvestigators.${index}.email`, '');
      setValue(`coInvestigators.${index}.run`, '');
      setValue(`coInvestigators.${index}.affiliation`, '');
      setValue(`coInvestigators.${index}.facultadUC`, '');
      setValue(`coInvestigators.${index}.unidadAcademicaUC`, '');
      setValue(`coInvestigators.${index}.doctorateYear`, '' as any);
      setValue(`coInvestigators.${index}.institutionType`, undefined);

      // Clear RUN input state for COI (they don't use the search input)
      setRunInputs((prev) => ({ ...prev, [index]: '' }));

      // Clear custom institution
      setCustomInstitutions((prev) => {
        const newCustom = { ...prev };
        delete newCustom[index];
        return newCustom;
      });
    }
  };

  return (
    <div>
      <h2 className={styles.sectionTitle}>Equipo de Investigación</h2>

      {/*
        DEV NOTE — Info box generado desde Contest.config (no texto libre)
        En producción este bloque debe derivarse de Contest.config, NO ser un
        campo de texto libre editable por el admin. Cada bullet corresponde a
        un valor configurable:
          · "Máximo 4 investigadores UC"  → Contest.config.maxUCResearchers
          · "máximo 2 proyectos"          → Contest.config.maxProjectsPerAcademic
          · "doctorado en 2020 o después" → Contest.config.classificationThreshold.year
          · roles visibles (IP, IA, COI)  → Contest.config.roles
        Esto garantiza que lo que lee el postulante nunca quede desincronizado
        con las validaciones reales del sistema.
      */}
      <div className={styles.sectionInfoBox}>
        <strong>Información importante:</strong>
        <p style={{ margin: '0.5rem 0' }}>
          El equipo de investigación UC puede estar conformado por Investigador
          Responsable (quien lidera el equipo), Investigador Principal e
          Investigador Asociado. Este equipo puede estar conformado por un
          máximo de 4 académicos UC.
        </p>
        <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
          <li>
            <strong>Investigador Responsable (IR):</strong> Académico/a UC que
            lidera la postulación. Debe contar con grado de doctor o
            subespecialidad médica, puede pertenecer a la Categoría A o B según
            corresponda.
          </li>
          <li>
            <strong>Investigador Principal (IP):</strong> Académico/a UC. Debe
            contar con grado de doctor o subespecialidad médica, puede
            pertenecer a la Categoría A o B según corresponda.
          </li>
          <li>
            <strong>Investigador Asociado (IA):</strong> Académico/a UC sin
            grado de doctor o subespecialidad médica.
          </li>
          <li>
            Cada académico UC puede participar en{' '}
            <strong>máximo 2 proyectos</strong> en cualquier rol (IR, IP o IA)
            en este concurso.
          </li>
          <li>
            <strong>Categoría A — Emergente:</strong> IR e IPs con doctorado
            obtenido desde 2020 en adelante.
          </li>
          <li>
            <strong>Categoría B — Consolidada:</strong> IR o algún IP con
            doctorado obtenido antes de 2020.
          </li>
        </ul>
      </div>

      <section className={styles.section}>
        <div className={styles.coInvestigatorCard}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>
              Investigador Responsable
            </h3>
          </div>
          {/* Fila 1: RUN + Rol badge */}
          <div className={styles.row}>
            <div className={styles.formGroup}>
              <label className={styles.label}>RUN *</label>
              <input
                {...register('principalInvestigator.run', {
                  required: 'El RUN es obligatorio',
                })}
                className={combineClasses(styles.input, styles.readOnlyField)}
                value={
                  principalInvestigator?.run
                    ? formatChileanRUT(principalInvestigator.run)
                    : ''
                }
                readOnly
              />
              {errors.principalInvestigator?.run && (
                <span className={styles.errorMessage}>
                  {errors.principalInvestigator.run.message}
                </span>
              )}
            </div>

          </div>

          {/* Fila 2: Nombre | Correo | Institución | ipDocRow (mismo patrón que IP) */}
          <div className={styles.row}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Nombre Completo *</label>
              <input
                {...register('principalInvestigator.name', {
                  required: 'El nombre completo es obligatorio',
                })}
                className={combineClasses(styles.input, styles.readOnlyField)}
                readOnly
              />
              {errors.principalInvestigator?.name && (
                <span className={styles.errorMessage}>
                  {errors.principalInvestigator.name.message}
                </span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Correo Electrónico *</label>
              <input
                {...register('principalInvestigator.email', {
                  required: 'El correo electrónico es obligatorio',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Formato de correo electrónico inválido',
                  },
                })}
                type="email"
                className={combineClasses(styles.input, styles.readOnlyField)}
                readOnly
              />
              {errors.principalInvestigator?.email && (
                <span className={styles.errorMessage}>
                  {errors.principalInvestigator.email.message}
                </span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Institución *</label>
              <input
                {...register('principalInvestigator.affiliation')}
                className={combineClasses(styles.input, styles.readOnlyField)}
                value="Pontificia Universidad Católica de Chile"
                readOnly
              />
            </div>

            <div className={styles.ipDocRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Mes del doctorado *</label>
                <input
                  type="text"
                  className={combineClasses(styles.input, styles.readOnlyField)}
                  value={principalInvestigator?.doctorateMonth ? MONTHS_ES[principalInvestigator.doctorateMonth - 1] : '—'}
                  readOnly
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Año del doctorado *</label>
                <input
                  {...register('principalInvestigator.doctorateYear', {
                    required: 'El año de obtención del doctorado es obligatorio',
                    valueAsNumber: true,
                    min: { value: 1950, message: 'El año debe ser mayor a 1950' },
                    max: { value: new Date().getFullYear(), message: 'El año no puede ser mayor al actual' },
                  })}
                  type="number"
                  className={combineClasses(styles.input, styles.readOnlyField)}
                  readOnly
                />
                {errors.principalInvestigator?.doctorateYear && (
                  <span className={styles.errorMessage}>
                    {errors.principalInvestigator.doctorateYear.message}
                  </span>
                )}
              </div>

              {principalInvestigator?.doctorateYear && (
                <div className={styles.ipDocHelper}>
                  <small>
                    {isConsolidated(principalInvestigator.doctorateYear, principalInvestigator.doctorateMonth)
                      ? 'Categoría B estimada (Consolidado) · DINV verifica categoría académica en admisibilidad'
                      : 'Categoría A estimada (Emergente) · DINV verifica categoría académica en admisibilidad'}
                  </small>
                </div>
              )}
            </div>
          </div>

          {/* Fila 4: Facultad + Unidad Académica */}
          {/* DEV NOTE (backend / Seba): "Facultad UC" y "Unidad Académica UC"
              son campos readOnly autollenados desde el perfil del investigador
              (userProfile.facultadUC / .unidadAcademica). Bug reportado por DINV
              (Checklist de Contenido, Fila 24, 2026-07-20): si el perfil NO trae
              la unidad, el campo queda vacío, es obligatorio y NO hay forma de
              cargarlo manualmente → bloquea el envío de la postulación. Se
              necesita decisión de la contraparte de datos: (a) garantizar que el
              perfil (CPA / datos institucionales) siempre traiga la unidad, o
              (b) permitir selección/carga manual como fallback (existe la lista
              en utils/unidades_academicas.json, hoy solo usada en Admin). Mismo
              patrón en los co-investigadores IP/IA más abajo. */}
          <div className={styles.row}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Facultad UC *</label>
              <input
                {...register('principalInvestigator.facultadUC', {
                  required: 'La facultad UC es obligatoria',
                })}
                className={combineClasses(styles.input, styles.readOnlyField)}
                readOnly
              />
              {errors.principalInvestigator?.facultadUC && (
                <span className={styles.errorMessage}>
                  {errors.principalInvestigator.facultadUC.message}
                </span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Unidad Académica UC *</label>
              <input
                {...register('principalInvestigator.unidadAcademicaUC', {
                  required: 'La unidad académica UC es obligatoria',
                })}
                className={combineClasses(styles.input, styles.readOnlyField)}
                readOnly
              />
              {errors.principalInvestigator?.unidadAcademicaUC && (
                <span className={styles.errorMessage}>
                  {errors.principalInvestigator.unidadAcademicaUC.message}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.headerContainer}>
          <h3 className={styles.headerTitle}>
            Integrantes adicionales del Equipo ({ucCoResearcherCount}/3 investigadores UC)
          </h3>
        </div>

        {coInvestigators.length === 0 ? (
          <div className={styles.emptyState}>
            No hay integrantes del equipo agregados
          </div>
        ) : (
          coInvestigators.map((_, index) => (
            <div key={index} className={styles.coInvestigatorCard}>
              <div className={styles.cardHeader}>
                <h3 className={styles.cardTitle}>
                  Integrante {index + 1}
                </h3>
                {!readOnly && (
                  <button
                    type="button"
                    className={styles.btnRemove}
                    onClick={() => removeCoInvestigator(index)}
                    aria-label="Eliminar miembro del equipo"
                  >
                    <i className="uc-icon icon-size--md icon-color--red">delete</i>
                  </button>
                )}
              </div>

              <div className={styles.row}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Rol en el Proyecto *</label>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        value="IP"
                        className={styles.radioInput}
                        {...register(`coInvestigators.${index}.role`, {
                          required: 'Debe seleccionar un rol',
                          validate: (value) => {
                            if (value === 'IP' || value === 'IA') {
                              const currentUCCount = coInvestigators.filter(
                                (_, i) =>
                                  i !== index &&
                                  ['IP', 'IA'].includes(watch(`coInvestigators.${i}.role`) ?? '')
                              ).length;
                              return (
                                currentUCCount < 3 ||
                                'Máximo 3 investigadores UC adicionales (IP + IA). El IR ocupa el 4° cupo.'
                              );
                            }
                            return true;
                          },
                        })}
                        onChange={(e) =>
                          handleRoleChange(index, e.target.value)
                        }
                        disabled={
                          readOnly ||
                          (watch(`coInvestigators.${index}.role`) !== 'IP' &&
                            ucCoResearcherCount >= 3)
                        }
                      />
                      Investigador Principal (IP)
                      {watch(`coInvestigators.${index}.role`) !== 'IP' &&
                        ucCoResearcherCount >= 3 && (
                          <small
                            style={{
                              color: 'var(--color-danger)',
                              fontSize: '0.75rem',
                            }}
                          >
                            (Máximo UC alcanzado)
                          </small>
                        )}
                    </label>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        value="IA"
                        className={styles.radioInput}
                        {...register(`coInvestigators.${index}.role`, {
                          required: 'Debe seleccionar un rol',
                        })}
                        onChange={(e) =>
                          handleRoleChange(index, e.target.value)
                        }
                        disabled={
                          readOnly ||
                          (watch(`coInvestigators.${index}.role`) !== 'IA' &&
                            ucCoResearcherCount >= 3)
                        }
                      />
                      Investigador Asociado (IA)
                      {watch(`coInvestigators.${index}.role`) !== 'IA' &&
                        ucCoResearcherCount >= 3 && (
                          <small
                            style={{
                              color: 'var(--color-danger)',
                              fontSize: '0.75rem',
                            }}
                          >
                            (Máximo UC alcanzado)
                          </small>
                        )}
                    </label>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        value="COI"
                        className={styles.radioInput}
                        {...register(`coInvestigators.${index}.role`, {
                          required: 'Debe seleccionar un rol',
                        })}
                        onChange={(e) =>
                          handleRoleChange(index, e.target.value)
                        }
                        disabled={readOnly}
                      />
                      Co-Investigador (COI) — Externo a la UC
                    </label>
                  </div>
                  {errors.coInvestigators?.[index]?.role && (
                    <span className={styles.errorMessage}>
                      {errors.coInvestigators[index]?.role?.message}
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.row}>
                <div className={styles.formGroup}>
                  <Select
                    id={`co-inv-${index}-documentType`}
                    label="Tipo de Documento"
                    required
                    options={[
                      { value: 'run', label: 'RUN' },
                      { value: 'passport', label: 'Pasaporte' },
                    ]}
                    error={errors.coInvestigators?.[index]?.documentType?.message}
                    {...register(`coInvestigators.${index}.documentType`, {
                      required: 'El tipo de documento es obligatorio',
                    })}
                    disabled={
                      readOnly ||
                      watch(`coInvestigators.${index}.role`) === 'IP' ||
                      watch(`coInvestigators.${index}.role`) === 'IA'
                    }
                    onChange={(e) => {
                      setValue(
                        `coInvestigators.${index}.documentType`,
                        e.target.value as 'run' | 'passport'
                      );
                      // Clear RUN field when changing document type
                      setValue(`coInvestigators.${index}.run`, '');
                      setRunInputs((prev) => ({ ...prev, [index]: '' }));
                      // Clear validation errors explicitly
                      clearErrors(`coInvestigators.${index}.run`);
                    }}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label
                    className={styles.label}
                    htmlFor={`co-inv-${index}-run`}
                  >
                    {(() => {
                      const role = watch(`coInvestigators.${index}.role`);
                      const docType = watch(
                        `coInvestigators.${index}.documentType`
                      );
                      if (role === 'IP' || role === 'IA') return 'RUN*';
                      return docType === 'passport' ? 'Pasaporte' : 'RUN*';
                    })()}
                  </label>
                  {(watch(`coInvestigators.${index}.role`) === 'IP' || watch(`coInvestigators.${index}.role`) === 'IA') ? (
                    <div className={styles.searchInputContainer}>
                      <input
                        className={combineClasses(
                          styles.input,
                          styles.searchInput,
                          errors.coInvestigators?.[index]?.run && styles.error,
                          readOnly && styles.readOnlyField
                        )}
                        id={`co-inv-${index}-run`}
                        value={(() => {
                          const currentRole = watch(
                            `coInvestigators.${index}.role`
                          );
                          const currentDocType = watch(
                            `coInvestigators.${index}.documentType`
                          );
                          const rawValue =
                            runInputs[index] ||
                            watch(`coInvestigators.${index}.run`) ||
                            '';
                          return shouldFormatAsChileanRUT(
                            currentRole,
                            currentDocType
                          )
                            ? formatChileanRUT(rawValue)
                            : rawValue;
                        })()}
                        onChange={(e) => {
                          const value = e.target.value;

                          // Normalize the RUN before saving to form state
                          const normalizedValue = normalizeChileanRUT(value);
                          setValue(
                            `coInvestigators.${index}.run`,
                            normalizedValue
                          );

                          // Clear runInputs so the display always uses the form state value
                          setRunInputs((prev) => {
                            const newInputs = { ...prev };
                            delete newInputs[index];
                            return newInputs;
                          });

                          clearResults(); // Clear previous results when typing
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const value = e.currentTarget.value.trim();
                            if (value) {
                              handleRunSearch(index, value);
                            }
                          }
                        }}
                        placeholder="Ej: 12.345.678-9"
                        disabled={
                          readOnly || (loading && searchingForIndex === index)
                        }
                        readOnly={readOnly}
                      />
                      <button
                        type="button"
                        className={combineClasses(
                          styles.searchButton,
                          loading &&
                            searchingForIndex === index &&
                            styles.searching,
                          profile && searchingForIndex === index
                            ? styles.success
                            : false
                        )}
                        onClick={() => {
                          const currentRole = watch(
                            `coInvestigators.${index}.role`
                          );
                          const currentDocType = watch(
                            `coInvestigators.${index}.documentType`
                          );
                          const rawValue =
                            runInputs[index] ||
                            watch(`coInvestigators.${index}.run`) ||
                            '';
                          const displayValue = shouldFormatAsChileanRUT(
                            currentRole,
                            currentDocType
                          )
                            ? formatChileanRUT(rawValue)
                            : rawValue;
                          if (displayValue.trim()) {
                            handleRunSearch(index, displayValue.trim());
                          }
                        }}
                        disabled={
                          readOnly ||
                          !(
                            runInputs[index]?.trim() ||
                            watch(`coInvestigators.${index}.run`)?.trim()
                          ) ||
                          (loading && searchingForIndex === index)
                        }
                        title="Buscar investigador"
                      >
                        {loading && searchingForIndex === index ? (
                          <span className={styles.spinner}></span>
                        ) : profile && searchingForIndex === index ? (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="20,6 9,17 4,12"></polyline>
                          </svg>
                        ) : (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <circle cx="11" cy="11" r="8"></circle>
                            <path d="m21 21-4.35-4.35"></path>
                          </svg>
                        )}
                      </button>
                      {error && searchingForIndex === index && (
                        <div className={styles.searchError}>{error}</div>
                      )}
                      {profile &&
                        searchingForIndex === index &&
                        !profile.canApply &&
                        profile.platformMessage && (
                          <div className={styles.searchError}>
                            {profile.platformMessage}
                          </div>
                        )}
                      {profile &&
                        searchingForIndex === index &&
                        profile.referencesCount &&
                        profile.referencesCount.total > 2 && (
                          <div className={styles.searchWarning}>
                            ⚠️ Aviso: Este investigador aparece en más de 2
                            postulaciones (
                            {profile.referencesCount?.byStatus.draft} en
                            borrador,{' '}
                            {profile.referencesCount.total -
                              profile.referencesCount?.byStatus.draft}{' '}
                            enviadas).
                            {profile.referencesCount.total -
                              profile.referencesCount?.byStatus.draft <
                            2
                              ? `Considera la distribución de equipos al momento de la postulación final.`
                              : `Un investigador no puede participar en más de 2 postulaciones.`}
                          </div>
                        )}
                    </div>
                  ) : (
                    <div className={styles.searchInputContainer}>
                      <input
                        className={combineClasses(
                          styles.input,
                          errors.coInvestigators?.[index]?.run && styles.error,
                          readOnly && styles.readOnlyField
                        )}
                        id={`co-inv-${index}-run`}
                        value={(() => {
                          const currentRole = watch(
                            `coInvestigators.${index}.role`
                          );
                          const currentDocType = watch(
                            `coInvestigators.${index}.documentType`
                          );
                          const rawValue =
                            runInputs[index] ||
                            watch(`coInvestigators.${index}.run`) ||
                            '';
                          return shouldFormatAsChileanRUT(
                            currentRole,
                            currentDocType
                          )
                            ? formatChileanRUT(rawValue)
                            : rawValue;
                        })()}
                        onChange={(e) => {
                          const value = e.target.value;
                          const currentDocType = watch(
                            `coInvestigators.${index}.documentType`
                          );

                          // Only normalize and validate RUN if documentType is 'run'
                          // For passport, just save the raw value
                          if (currentDocType === 'run') {
                            // Normalize the RUN before saving to form state
                            const normalizedValue = normalizeChileanRUT(value);
                            setValue(
                              `coInvestigators.${index}.run`,
                              normalizedValue
                            );

                            // Only validate if we have a complete RUN (8-9 digits with hyphen)
                            const cleanValue = normalizedValue.replace(
                              /[^0-9kK]/g,
                              ''
                            );
                            if (
                              cleanValue.length >= 8 &&
                              cleanValue.length <= 9
                            ) {
                              // We have a complete RUN, validate it
                              if (!validateChileanRUT(normalizedValue)) {
                                setError(`coInvestigators.${index}.run`, {
                                  type: 'validate',
                                  message: 'El RUT ingresado no es válido',
                                });
                              } else {
                                clearErrors(`coInvestigators.${index}.run`);
                              }
                            } else {
                              // Incomplete RUN while typing, clear any validation errors
                              clearErrors(`coInvestigators.${index}.run`);
                            }
                          } else {
                            // Passport: save raw value, no normalization or validation
                            setValue(`coInvestigators.${index}.run`, value);
                            clearErrors(`coInvestigators.${index}.run`);
                          }

                          // Clear runInputs so the display always uses the form state value
                          setRunInputs((prev) => {
                            const newInputs = { ...prev };
                            delete newInputs[index];
                            return newInputs;
                          });

                          clearResults(); // Clear previous results when typing

                          // Update COI search results based on input
                          if (value.trim() && !isChileanRUT(value)) {
                            // International passport - no search needed
                            setCOISearchResults((prev) => ({
                              ...prev,
                              [index]: 'no-search',
                            }));
                          } else {
                            // Chilean RUT or empty input - clear previous search results but don't mark as searching yet
                            setCOISearchResults((prev) => {
                              const newResults = { ...prev };
                              delete newResults[index];
                              return newResults;
                            });
                          }
                        }}
                        onBlur={(e) => {
                          const currentDocType = watch(
                            `coInvestigators.${index}.documentType`
                          );
                          const formValue = watch(
                            `coInvestigators.${index}.run`
                          );

                          // Validate on blur only for RUN documentType
                          if (currentDocType === 'run') {
                            if (!formValue || !formValue.trim()) {
                              setError(`coInvestigators.${index}.run`, {
                                type: 'required',
                                message: 'El RUN es requerido',
                              });
                            } else if (!validateChileanRUT(formValue)) {
                              setError(`coInvestigators.${index}.run`, {
                                type: 'validate',
                                message: 'El RUT ingresado no es válido',
                              });
                            } else {
                              clearErrors(`coInvestigators.${index}.run`);
                            }
                          } else {
                            // For passport, no validation needed - clear any errors
                            clearErrors(`coInvestigators.${index}.run`);
                          }

                          // COI members do not auto-search on blur
                          // They must manually enter their data (unlike IP members)
                        }}
                        placeholder={
                          watch(`coInvestigators.${index}.documentType`) ===
                          'passport'
                            ? 'Número de Pasaporte'
                            : 'Ej: 12.345.678-9'
                        }
                        readOnly={readOnly}
                      />
                      {error && searchingForIndex === index && (
                        <div className={styles.searchError}>{error}</div>
                      )}
                      {profile &&
                        searchingForIndex === index &&
                        watch(`coInvestigators.${index}.role`) === 'COI' && (
                          <div className={styles.searchWarning}>
                            ⚠️ El RUT ingresado es de un académico UC. Los
                            académicos UC sólo pueden ser Investigadores
                            Principales; los externos pueden ser
                            Co-Investigadores.
                          </div>
                        )}
                    </div>
                  )}
                  {errors.coInvestigators?.[index]?.run && (
                    <span className={styles.errorMessage}>
                      {errors.coInvestigators[index]?.run?.message}
                    </span>
                  )}
                  <small className={styles.helpText}>
                    {(() => {
                      const role = watch(`coInvestigators.${index}.role`);
                      const docType = watch(
                        `coInvestigators.${index}.documentType`
                      );

                      if (role === 'IP') {
                        return 'Ingresa el RUT y presiona el botón de búsqueda o Enter para obtener los datos del investigador';
                      }

                      return docType === 'passport'
                        ? 'Número de pasaporte (opcional)'
                        : 'RUT, se verificará automáticamente';
                    })()}
                  </small>
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.formGroup}>
                  <label
                    className={styles.label}
                    htmlFor={`co-inv-${index}-name`}
                  >
                    Nombre Completo *
                  </label>
                  <input
                    className={combineClasses(
                      styles.input,
                      errors.coInvestigators?.[index]?.name && styles.error,
                      (readOnly ||
                        watch(`coInvestigators.${index}.role`) === 'IP' ||
                        watch(`coInvestigators.${index}.role`) === 'IA' ||
                        (watch(`coInvestigators.${index}.role`) === 'COI' &&
                          !shouldEnableCOIFields(index))) &&
                        styles.readOnlyField
                    )}
                    id={`co-inv-${index}-name`}
                    {...register(`coInvestigators.${index}.name`, {
                      required: 'El nombre es requerido',
                      validate: (value) => {
                        const r = watch(`coInvestigators.${index}.role`);
                        if (r === 'IP' || r === 'IA') {
                          const hasRun = watch(`coInvestigators.${index}.run`);
                          if (hasRun && !value) {
                            return 'Debe completar la búsqueda del investigador';
                          }
                        }
                        return true;
                      },
                    })}
                    placeholder={
                      watch(`coInvestigators.${index}.role`) === 'IP' ||
                      watch(`coInvestigators.${index}.role`) === 'IA'
                        ? ''
                        : 'Nombre y apellidos completos'
                    }
                    readOnly={
                      readOnly ||
                      watch(`coInvestigators.${index}.role`) === 'IP' ||
                      watch(`coInvestigators.${index}.role`) === 'IA' ||
                      (watch(`coInvestigators.${index}.role`) === 'COI' &&
                        !shouldEnableCOIFields(index))
                    }
                  />
                  {errors.coInvestigators?.[index]?.name && (
                    <span className={styles.errorMessage}>
                      {errors.coInvestigators[index]?.name?.message}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label
                    className={styles.label}
                    htmlFor={`co-inv-${index}-email`}
                  >
                    Correo Electrónico *
                  </label>
                  <input
                    className={combineClasses(
                      styles.input,
                      errors.coInvestigators?.[index]?.email && styles.error,
                      (readOnly ||
                        ((watch(`coInvestigators.${index}.role`) === 'IP' ||
                          watch(`coInvestigators.${index}.role`) === 'IA') &&
                          watch(`coInvestigators.${index}.name`) === '') ||
                        (watch(`coInvestigators.${index}.role`) === 'COI' &&
                          !shouldEnableCOIFields(index))) &&
                        styles.readOnlyField
                    )}
                    id={`co-inv-${index}-email`}
                    type="email"
                    {...register(`coInvestigators.${index}.email`, {
                      required: 'El correo electrónico es requerido',
                      pattern:
                        watch(`coInvestigators.${index}.role`) === 'IP' ||
                        watch(`coInvestigators.${index}.role`) === 'IA'
                          ? {
                              value: /^[A-Z0-9._%+-]+@uc\.cl$/i,
                              message:
                                'Los investigadores UC deben usar correo @uc.cl',
                            }
                          : {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Ingrese un correo electrónico válido',
                            },
                    })}
                    placeholder={
                      watch(`coInvestigators.${index}.role`) === 'IP' ||
                      watch(`coInvestigators.${index}.role`) === 'IA'
                        ? ''
                        : 'usuario@universidad.cl'
                    }
                    readOnly={
                      readOnly ||
                      ((watch(`coInvestigators.${index}.role`) === 'IP' ||
                        watch(`coInvestigators.${index}.role`) === 'IA') &&
                        watch(`coInvestigators.${index}.name`) === '') ||
                      (watch(`coInvestigators.${index}.role`) === 'COI' &&
                        !shouldEnableCOIFields(index))
                    }
                  />
                  {errors.coInvestigators?.[index]?.email && (
                    <span className={styles.errorMessage}>
                      {errors.coInvestigators[index]?.email?.message}
                    </span>
                  )}
                </div>

                {(watch(`coInvestigators.${index}.role`) === 'IP' || watch(`coInvestigators.${index}.role`) === 'IA') ? (
                  <div className={styles.formGroup}>
                    <label
                      className={styles.label}
                      htmlFor={`co-inv-${index}-affiliation`}
                    >
                      Institución *
                    </label>
                    <input
                      className={combineClasses(
                        styles.input,
                        styles.readOnlyField
                      )}
                      id={`co-inv-${index}-affiliation`}
                      {...register(`coInvestigators.${index}.affiliation`)}
                      value="Pontificia Universidad Católica de Chile"
                      readOnly
                    />
                  </div>
                ) : (
                  <>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>
                        Tipo de Institución *
                      </label>
                      <div className={styles.radioGroup}>
                        <label className={styles.radioLabel}>
                          <input
                            type="radio"
                            value="Nacional"
                            className={styles.radioInput}
                            {...register(
                              `coInvestigators.${index}.institutionType`,
                              {
                                required:
                                  watch(`coInvestigators.${index}.role`) ===
                                  'COI'
                                    ? 'El tipo de institución es requerido'
                                    : false,
                              }
                            )}
                            disabled={readOnly || !shouldEnableCOIFields(index)}
                          />
                          Nacional
                        </label>
                        <label className={styles.radioLabel}>
                          <input
                            type="radio"
                            value="Internacional"
                            className={styles.radioInput}
                            {...register(
                              `coInvestigators.${index}.institutionType`,
                              {
                                required:
                                  watch(`coInvestigators.${index}.role`) ===
                                  'COI'
                                    ? 'El tipo de institución es requerido'
                                    : false,
                              }
                            )}
                            disabled={readOnly || !shouldEnableCOIFields(index)}
                          />
                          Extranjera
                        </label>
                      </div>
                      {errors.coInvestigators?.[index]?.institutionType && (
                        <span className={styles.errorMessage}>
                          {
                            errors.coInvestigators[index]?.institutionType
                              ?.message
                          }
                        </span>
                      )}
                    </div>
                    <div className={styles.formGroup}>
                      {/* Hidden input for react-hook-form registration */}
                      <input
                        type="hidden"
                        {...register(`coInvestigators.${index}.affiliation`, {
                          required:
                            watch(`coInvestigators.${index}.role`) !== 'IP' &&
                            watch(`coInvestigators.${index}.role`) !== 'IA'
                              ? 'La institución es requerida'
                              : false,
                        })}
                      />
                      <SearchableSelect
                        id={`co-inv-${index}-affiliation`}
                        label="Institución/Unidad Académica"
                        required
                        placeholder="Buscar institución..."
                        options={
                          watch(`coInvestigators.${index}.institutionType`) ===
                          'Nacional'
                            ? nationalInstitutions
                            : internationalInstitutions
                        }
                        error={!!errors.coInvestigators?.[index]?.affiliation}
                        helperText={
                          errors.coInvestigators?.[index]?.affiliation
                            ?.message ||
                          `Seleccionar "${otherInstitutionsOption}" si no aparece en el listado`
                        }
                        value={(() => {
                          const affiliation =
                            watch(`coInvestigators.${index}.affiliation`) || '';
                          // If it's a custom "Otras Instituciones - XXX", display just "Otras Instituciones"
                          if (
                            affiliation.startsWith(
                              `${otherInstitutionsOption} - `
                            )
                          ) {
                            return otherInstitutionsOption;
                          }
                          return affiliation;
                        })()}
                        onSelect={(value) => {
                          if (!readOnly && shouldEnableCOIFields(index)) {
                            setValue(
                              `coInvestigators.${index}.affiliation`,
                              value,
                              { shouldDirty: true, shouldValidate: true }
                            );
                            trigger(`coInvestigators.${index}.affiliation`);

                            // Clear custom institution when changing selection
                            if (value !== otherInstitutionsOption) {
                              setCustomInstitutions((prev) => {
                                const newCustom = { ...prev };
                                delete newCustom[index];
                                return newCustom;
                              });
                            }
                          }
                        }}
                        setUpdating={setUpdating}
                        disabled={readOnly || !shouldEnableCOIFields(index)}
                      />
                      {watch(`coInvestigators.${index}.role`) === 'COI' &&
                        (watch(`coInvestigators.${index}.affiliation`) ===
                          otherInstitutionsOption ||
                          watch(
                            `coInvestigators.${index}.affiliation`
                          )?.startsWith(`${otherInstitutionsOption} - `)) && (
                          <div style={{ marginTop: '0.5rem' }}>
                            <label
                              className={styles.label}
                              htmlFor={`co-inv-${index}-custom-institution`}
                            >
                              Ingrese institución:
                            </label>
                            <input
                              className={combineClasses(
                                styles.input,
                                readOnly && styles.readOnlyField
                              )}
                              id={`co-inv-${index}-custom-institution`}
                              value={customInstitutions[index] || ''}
                              onChange={(e) => {
                                const customValue = e.target.value;
                                setCustomInstitutions((prev) => ({
                                  ...prev,
                                  [index]: customValue,
                                }));
                              }}
                              onBlur={(e) => {
                                // Update the affiliation value on blur
                                const trimmedValue = e.target.value.trim();
                                if (trimmedValue) {
                                  const newAffiliation = `${otherInstitutionsOption} - ${trimmedValue}`;
                                  setValue(
                                    `coInvestigators.${index}.affiliation`,
                                    newAffiliation,
                                    { shouldDirty: true, shouldValidate: true }
                                  );
                                  // Update custom institution with trimmed value
                                  setCustomInstitutions((prev) => ({
                                    ...prev,
                                    [index]: trimmedValue,
                                  }));
                                } else {
                                  // If empty, just set to "Otras Instituciones"
                                  setValue(
                                    `coInvestigators.${index}.affiliation`,
                                    otherInstitutionsOption,
                                    { shouldDirty: true, shouldValidate: true }
                                  );
                                }
                              }}
                              placeholder="Nombre de la institución"
                              readOnly={
                                readOnly || !shouldEnableCOIFields(index)
                              }
                              disabled={
                                readOnly || !shouldEnableCOIFields(index)
                              }
                            />
                          </div>
                        )}
                    </div>
                  </>
                )}

                {watch(`coInvestigators.${index}.role`) === 'IP' && (
                  <div className={styles.ipDocRow}>
                    <div className={styles.formGroup}>
                      <label
                        className={styles.label}
                        htmlFor={`co-inv-${index}-doctorate-month`}
                      >
                        Mes del doctorado *
                      </label>
                      <input
                        id={`co-inv-${index}-doctorate-month`}
                        type="text"
                        className={combineClasses(styles.input, styles.readOnlyField)}
                        value={(() => { const m = watch(`coInvestigators.${index}.doctorateMonth`); return m ? MONTHS_ES[m - 1] : '—'; })()}
                        readOnly
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label
                        className={styles.label}
                        htmlFor={`co-inv-${index}-doctorate`}
                      >
                        Año del doctorado *
                      </label>
                      <input
                        className={combineClasses(
                          styles.input,
                          errors.coInvestigators?.[index]?.doctorateYear && styles.error,
                          (readOnly || watch(`coInvestigators.${index}.role`) === 'IP') && styles.readOnlyField
                        )}
                        id={`co-inv-${index}-doctorate`}
                        type="number"
                        min="1970"
                        max={new Date().getFullYear()}
                        {...register(`coInvestigators.${index}.doctorateYear`, {
                          required:
                            watch(`coInvestigators.${index}.role`) === 'IP'
                              ? 'El año de doctorado es requerido'
                              : false,
                          min: { value: 1970, message: 'Ingrese un año válido' },
                          max: { value: new Date().getFullYear(), message: 'El año no puede ser futuro' },
                        })}
                        placeholder={watch(`coInvestigators.${index}.role`) === 'IP' ? '' : 'Ej: 2015'}
                        readOnly={readOnly || watch(`coInvestigators.${index}.role`) === 'IP'}
                      />
                      {errors.coInvestigators?.[index]?.doctorateYear && (
                        <span className={styles.errorMessage}>
                          {errors.coInvestigators[index]?.doctorateYear?.message}
                        </span>
                      )}
                    </div>
                    {/* Helper de categoría: abarca ambas columnas */}
                    {(() => {
                      const yr = watch(`coInvestigators.${index}.doctorateYear`);
                      const mo = watch(`coInvestigators.${index}.doctorateMonth`);
                      if (!yr) return null;
                      const irIsConsolidated = isConsolidated(
                        principalInvestigator?.doctorateYear,
                        principalInvestigator?.doctorateMonth
                      );
                      const thisIPIsConsolidated = isConsolidated(yr, mo);
                      const thisIPDetermines = thisIPIsConsolidated && !irIsConsolidated;
                      return (
                        <div className={styles.ipDocHelper}>
                          <small>
                            {thisIPIsConsolidated
                              ? thisIPDetermines
                                ? 'Categoría B estimada (Consolidado) · Este investigador determina que el equipo compita en Categoría B · DINV confirma en admisibilidad'
                                : 'Categoría B estimada (Consolidado) · DINV confirma en admisibilidad'
                              : 'Categoría A estimada (Emergente) · DINV confirma en admisibilidad'}
                          </small>
                        </div>
                      );
                    })()}
                  </div>
                )}
                {(watch(`coInvestigators.${index}.role`) === 'IP' || watch(`coInvestigators.${index}.role`) === 'IA') && (
                  <>
                    <div className={styles.formGroup}>
                      <label
                        className={styles.label}
                        htmlFor={`co-inv-${index}-facultad`}
                      >
                        Facultad UC *
                      </label>
                      <input
                        className={combineClasses(
                          styles.input,
                          errors.coInvestigators?.[index]?.facultadUC &&
                            styles.error,
                          styles.readOnlyField
                        )}
                        id={`co-inv-${index}-facultad`}
                        {...register(`coInvestigators.${index}.facultadUC`, {
                          required:
                            watch(`coInvestigators.${index}.role`) === 'IP' ||
                            watch(`coInvestigators.${index}.role`) === 'IA'
                              ? 'La facultad UC es requerida'
                              : false,
                        })}
                        readOnly
                      />
                      {errors.coInvestigators?.[index]?.facultadUC && (
                        <span className={styles.errorMessage}>
                          {errors.coInvestigators[index]?.facultadUC?.message}
                        </span>
                      )}
                    </div>

                    <div className={styles.formGroup}>
                      <label
                        className={styles.label}
                        htmlFor={`co-inv-${index}-unidad`}
                      >
                        Unidad Académica UC *
                      </label>
                      <input
                        className={combineClasses(
                          styles.input,
                          errors.coInvestigators?.[index]?.unidadAcademicaUC &&
                            styles.error,
                          styles.readOnlyField
                        )}
                        id={`co-inv-${index}-unidad`}
                        {...register(
                          `coInvestigators.${index}.unidadAcademicaUC`,
                          {
                            required:
                              watch(`coInvestigators.${index}.role`) === 'IP' ||
                              watch(`coInvestigators.${index}.role`) === 'IA'
                                ? 'La unidad académica UC es requerida'
                                : false,
                          }
                        )}
                        readOnly
                      />
                      {errors.coInvestigators?.[index]?.unidadAcademicaUC && (
                        <span className={styles.errorMessage}>
                          {
                            errors.coInvestigators[index]?.unidadAcademicaUC
                              ?.message
                          }
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          ))
        )}

        {!readOnly && (
          <Button
            type="button"
            variant="primary"
            onClick={addCoInvestigator}
            disabled={readOnly}
            style={{ marginTop: '1em' }}
          >
            + Agregar Integrante
          </Button>
        )}

        {ucCoResearcherCount >= 3 && (
          <small className={styles.helpText} style={{ marginTop: '0.5rem' }}>
            Se alcanzó el máximo de 3 investigadores UC adicionales (IP + IA).
            Aún es posible agregar Co-Investigadores (COI) externos.
          </small>
        )}

        <div
          className={combineClasses(
            styles.teamCategoryBanner,
            teamType === 'consolidated' ? styles.consolidated : styles.emerging
          )}
        >
          <strong>
            {teamType === 'consolidated'
              ? 'Categoría B — Equipo Consolidado'
              : 'Categoría A — Equipo Emergente'}
          </strong>
          <span className={styles.teamTypeCause}>
            {teamType === 'consolidated'
              ? 'El IR o al menos un IP del equipo tiene doctorado anterior a 2020. DINV confirma en admisibilidad.'
              : 'IR e IPs del equipo con doctorado desde 2020. DINV confirma en admisibilidad.'}
          </span>
        </div>

        {!hasInternationalCOI && (
          <div className={styles.coiWarningBox}>
            <p className={styles.coiWarningTitle}>
              Proyecto no presenta colaboración internacional
            </p>
            <p className={styles.coiWarningText}>
              Este concurso incentiva la colaboración internacional. Se espera
              que el equipo incluya al menos un Co-Investigador (COI) de
              institución extranjera; de no ser posible, justifique.
            </p>
            <label className={styles.label} htmlFor="internationalCoiJustification">
              Justificación
              {!readOnly && <span style={{ color: 'var(--color-danger)' }}> *</span>}
            </label>
            <textarea
              id="internationalCoiJustification"
              className={styles.coiJustificationTextarea}
              readOnly={readOnly}
              placeholder=""
              {...register('internationalCoiJustification', {
                validate: (val) =>
                  hasInternationalCOI ||
                  !!(val && val.trim().length > 0) ||
                  'Este campo es obligatorio',
              })}
            />
            {errors.internationalCoiJustification && (
              <span className={styles.errorMessage}>
                {errors.internationalCoiJustification.message}
              </span>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default TeamInformation;
